import {
  isBefore,
  isAfter,
  isSameDay,
  addYears,
  getYear,
  differenceInCalendarWeeks,
} from "date-fns";

import appLanguages from "./app-languages";
import pad from "./pad";
import valueIsNonEmptyString from "./value-is-non-empty-string";

const IsDate = (date) => {
  if (!date) return false;
  if (typeof date.getFullYear !== "function") {
    return false;
  }
  const year = date.getFullYear();
  return !isNaN(year);
};
//takes a date and uses GMT values to get a date that came
//in GMT timezone and make it behave in browser as midnight on
//that date according to the local timezone
const toBrowserPureDate = (date) => {
  if (IsDate(date) && !date.localMidnight) {
    let returner = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      0,
      0,
      0,
      0
    );
    returner.localMidnight = true;
    returner.gmtMidnight = false;
    return returner;
  } else if (IsDate(date) && date.localMidnight) {
    // eslint-disable-next-line no-param-reassign
    date.gmtMidnight = false;
    return date;
  }
  return undefined;
};

const serverStringToBrowserDate = (string) =>
  toBrowserPureDate(new Date(string));

const uiDateToGmtIsoString = (date) => {
  if (!IsDate(date)) {
    return "";
  }
  const month = pad(date.getMonth() + 1, 2, "0");
  const day = pad(date.getDate(), 2, "0");
  return `${date.getFullYear()}-${month}-${day}T00:00:00Z`;
};

//expects a browser date from everything in the react app now!
const prepDateForSubmission = (date) => {
  if (!IsDate(date)) {
    return "";
  }
  return uiDateToGmtIsoString(date);
};

const defaultDateDisplayOptions = {
  day: "2-digit",
  month: "long",
  year: "numeric",
};

const dateLocaleAliases = {
  cb: "ceb",
  ceb: "ceb",
  nid: "nl",
  nld: "nl",
  no: "no",
  nor: "no",
  tgl: "fil",
  tl: "fil",
  zh: "zh",
  zho: "zh",
  zhs: "zh-CN",
};

const languageCodeToLocale = (languageCode) => {
  if (!valueIsNonEmptyString(languageCode)) {
    return undefined;
  }

  const lowerLanguageCode = languageCode.toLowerCase();
  if (dateLocaleAliases[lowerLanguageCode]) {
    return dateLocaleAliases[lowerLanguageCode];
  }

  const appLanguage = appLanguages.find((language) =>
    language.code3 === lowerLanguageCode || language.code === lowerLanguageCode
  );
  if (!appLanguage) {
    return lowerLanguageCode;
  }

  return dateLocaleAliases[appLanguage.code] ?? appLanguage.code;
};

const buildCertificateDayMonthYearDate = ({ day, month, year }) =>
  `${day} ${month} ${year}`;

const buildCertificateOrdinalDayMonthYearDate = ({ day, month, year }) =>
  `${day}. ${month} ${year}`;

const buildCertificateDottedDayMonthYearDate = ({ day, month, year }) =>
  `${day}.${month}.${year}`;

const buildCertificateJapaneseDate = ({ day, month, year }) =>
  `${year}年${month}月${day}日`;

const buildCertificateKoreanDate = ({ day, month, year }) =>
  `${year}.${month}.${day}`;

const certificateDateFormatOverrides = {
  cs: {
    locale: "cs-CZ",
    options: { day: "numeric", month: "long", year: "numeric" },
    build: buildCertificateOrdinalDayMonthYearDate,
  },
  de: {
    locale: "de-DE",
    options: { day: "2-digit", month: "2-digit", year: "numeric" },
    build: buildCertificateDottedDayMonthYearDate,
  },
  fi: {
    locale: "fi-FI",
    options: { day: "numeric", month: "long", year: "numeric" },
    build: buildCertificateOrdinalDayMonthYearDate,
  },
  fr: {
    locale: "fr-FR",
    options: { day: "numeric", month: "long", year: "numeric" },
    build: buildCertificateDayMonthYearDate,
  },
  it: {
    locale: "it-IT",
    options: { day: "2-digit", month: "long", year: "numeric" },
    build: buildCertificateDayMonthYearDate,
  },
  ja: {
    locale: "ja-JP-u-ca-gregory",
    options: { day: "2-digit", month: "2-digit", year: "numeric" },
    build: buildCertificateJapaneseDate,
  },
  ko: {
    locale: "ko-KR",
    options: { day: "numeric", month: "numeric", year: "numeric" },
    build: buildCertificateKoreanDate,
  },
  no: {
    locale: "no",
    options: { day: "numeric", month: "long", year: "numeric" },
    build: buildCertificateOrdinalDayMonthYearDate,
  },
  th: {
    locale: "th-TH-u-ca-buddhist",
    options: { day: "numeric", month: "long", year: "numeric" },
    build: buildCertificateDayMonthYearDate,
  },
};

const getSupportedDateLocale = (languageCode, preferredLocale) => {
  const mappedLocale = languageCodeToLocale(languageCode);
  const localeCandidates = [
    preferredLocale,
    preferredLocale?.split("-u-")[0],
    mappedLocale,
    mappedLocale?.split("-")[0],
  ].filter(valueIsNonEmptyString);
  const supportedLocales = Intl.DateTimeFormat.supportedLocalesOf(localeCandidates);
  return supportedLocales[0];
};

const formatDateWithLanguageLocale = (date, options, languageCode, preferredLocale) => {
  if (!IsDate(date)) {
    return "";
  }

  try {
    const locale = getSupportedDateLocale(languageCode, preferredLocale);
    if (valueIsNonEmptyString(locale)) {
      return new Intl.DateTimeFormat(locale, options).format(date);
    }
  } catch {
    // Fall through to the browser-default formatter below.
  }

  return date.toLocaleDateString(undefined, options);
};

const mapDateFormatParts = (parts) => {
  const formattedParts = {};
  parts.forEach((part) => {
    if (part.type !== "literal" && !formattedParts[part.type]) {
      formattedParts[part.type] = part.value;
    }
  });
  return formattedParts;
};

const DateToDisplayString = (date, options = defaultDateDisplayOptions, languageCode) => {
  return formatDateWithLanguageLocale(date, options, languageCode);
};

const formatCertificateDate = (date, languageCode) => {
  if (!IsDate(date)) {
    return "";
  }

  const mappedLocale = languageCodeToLocale(languageCode);
  const override = certificateDateFormatOverrides[mappedLocale];
  if (!override) {
    return formatDateWithLanguageLocale(date, defaultDateDisplayOptions, languageCode);
  }

  try {
    const locale = getSupportedDateLocale(languageCode, override.locale);
    if (!valueIsNonEmptyString(locale)) {
      return formatDateWithLanguageLocale(date, defaultDateDisplayOptions, languageCode, override.locale);
    }

    const formatter = new Intl.DateTimeFormat(locale, override.options);
    const parts = mapDateFormatParts(formatter.formatToParts(date));
    return override.build(parts);
  } catch {
    return formatDateWithLanguageLocale(date, defaultDateDisplayOptions, languageCode, override.locale);
  }
};

const prepObjectDatePropsForSubmission = (obj) => {
  const newObj = { ...obj };
  for (var prop in newObj) {
    // eslint-disable-next-line no-prototype-builtins
    if (newObj.hasOwnProperty(prop)) {
      if (IsDate(newObj[prop])) {
        newObj[prop] = prepDateForSubmission(newObj[prop]);
      }
    }
  }
  return newObj;
};
const DateToTimeDisplay = (date, includeSeconds) => {

  const options = {
    hour: "2-digit",
    minute: "numeric",
    hour12: true,
  };
  if (includeSeconds) {
    options.second = "numeric";
  }
  const timeString = date.toLocaleString("en-US", options);

  return timeString;
}
const twentyFourToTwelve = (time) => {
  const date = new Date("01/01/0000 " + time);
  return DateToTimeDisplay(date);
};


const isDateInRange = (dateToCheck, minDate, maxDate) => {
  if (!IsDate(dateToCheck)) return false;
  //if we don't have valid min and max dates we return true.
  if (!IsDate(minDate) || !IsDate(maxDate)) {
    return true;
  }
  const useMinDate = minDate;
  const useMaxDate = maxDate;
  const useDateToCheck = dateToCheck;

  const minPasses =
    isSameDay(useDateToCheck, useMinDate) ||
    isAfter(useDateToCheck, useMinDate);
  const maxPasses =
    isSameDay(useDateToCheck, useMaxDate) ||
    isBefore(useDateToCheck, useMaxDate);
  return minPasses && maxPasses;
};
const parseTimeStringToMinutes = (timeString) => {
  if (!valueIsNonEmptyString(timeString)) {
    return -1;
  }
  let [hours = -1, minutes = -1] = timeString.split(":");
  hours = parseInt(hours);
  minutes = parseInt(minutes);
  if (hours < 0 || minutes < 0) {
    return -1;
  }
  return hours * 60 + minutes;
};
const getWeekForDate = (date) => {
  const useDate = date;
  const year = useDate.getUTCFullYear();
  const jan1 = new Date(year, 0, 1);
  const week = differenceInCalendarWeeks(date, jan1) + 1;
  const result = `${year}_${pad(week, 2)}`;
  return result;
};
const today = new Date();
const latestBirthYear = addYears(new Date(getYear(today),0,1),-11);//don't let birthday be more recent than 11 years ago
//we have one birthday input that is using the eden date input directly
//and migrating it over on the search form turned out explosively bad on the first try
const latestBirthYearString = latestBirthYear.toISOString().split('T')[0]

const latestSisBirthYear = addYears(new Date(getYear(today),0,1),-10);
const latestSisBirthYearString = latestSisBirthYear.toISOString().split('T')[0]

export {
  DateToDisplayString,
  DateToTimeDisplay,
  formatCertificateDate,
  getWeekForDate,
  parseTimeStringToMinutes,
  IsDate,
  prepObjectDatePropsForSubmission,
  twentyFourToTwelve,
  prepDateForSubmission,
  isDateInRange,
  uiDateToGmtIsoString,
  serverStringToBrowserDate,
  latestBirthYear,
  latestBirthYearString,
  latestSisBirthYear,
  latestSisBirthYearString,
  languageCodeToLocale
};
